// Display helpers for the analytics band: routes, words for ages and rates, and the geometry of the inline SVG
// chart and the segmented bars. No business math — every count, rate, band and classification arrives from the
// API (ADR 0003 §4); this file only turns them into pixels and words.
import type { Bucket, PursuitRef, WeekPoint } from '../types/pipeline'

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** Where a pursuit opens: the tab the API filed it under, then its id. */
export function routeForPursuit(p: Pick<PursuitRef, 'engagementId' | 'tab'>): string {
  return `/${p.tab}/${encodeURIComponent(p.engagementId)}`
}

/** "Sep 7" from YYYY-MM-DD without touching the local time zone. */
export function shortDate(iso: string): string {
  const [, m, d] = iso.slice(0, 10).split('-').map(Number)
  return `${MONTHS[m - 1]} ${d}`
}

/** A rate 0..1 as "43%"; null as an em dash. */
export function pct(rate: number | null | undefined): string {
  return rate == null ? '—' : `${Math.round(rate * 100)}%`
}

/** Days as words a business developer says: "3 days", "6 weeks", "4 months", "2 yr 3 mo". */
export function ageWords(days: number): string {
  if (days < 14) return `${days} day${days === 1 ? '' : 's'}`
  if (days < 60) return `${Math.round(days / 7)} weeks`
  if (days < 365) return `${Math.round(days / 30.4)} months`
  const years = Math.floor(days / 365)
  const months = Math.round((days - years * 365) / 30.4)
  return months > 0 ? `${years} yr ${months} mo` : `${years} yr`
}

/** Which weeks get a tick label: the first, every fourth after, and the last. */
export function weekTicks(points: WeekPoint[]): number[] {
  if (points.length === 0) return []
  const ticks: number[] = []
  for (let i = 0; i < points.length; i += 4) ticks.push(i)
  const last = points.length - 1
  if (ticks[ticks.length - 1] !== last) {
    if (last - ticks[ticks.length - 1] < 3) ticks.pop()   // two labels a week or two apart would collide
    ticks.push(last)
  }
  return ticks
}

export interface ChartBox { width: number; height: number; left: number; right: number; top: number; bottom: number }

/** x for the i-th of n points inside the box. */
export function xAt(i: number, n: number, box: ChartBox): number {
  if (n <= 1) return box.left
  return box.left + ((box.width - box.left - box.right) * i) / (n - 1)
}

/** y for a value against the max, baseline at the bottom of the box. */
export function yAt(value: number, max: number, box: ChartBox): number {
  const plot = box.height - box.top - box.bottom
  if (max <= 0) return box.top + plot
  return box.top + plot - (plot * value) / max
}

/** An SVG polyline "points" attribute for one series. */
export function linePoints(values: number[], max: number, box: ChartBox): string {
  return values.map((v, i) => `${xAt(i, values.length, box).toFixed(1)},${yAt(v, max, box).toFixed(1)}`).join(' ')
}

/** Clean axis ticks for a max: 0, and up to three round steps. */
export function yTicks(max: number): number[] {
  if (max <= 0) return [0]
  const step = niceStep(max)
  const ticks: number[] = []
  for (let v = 0; v <= max; v += step) ticks.push(v)
  if (ticks[ticks.length - 1] < max) ticks.push(ticks[ticks.length - 1] + step)
  return ticks
}

function niceStep(max: number): number {
  const raw = max / 3
  const mag = 10 ** Math.floor(Math.log10(Math.max(raw, 1)))
  const norm = raw / mag
  const nice = norm <= 1 ? 1 : norm <= 2.5 ? 2 : norm <= 5 ? 5 : 10
  return nice * mag
}

/** The index of the point nearest an x inside the box. */
export function nearestIndex(x: number, n: number, box: ChartBox): number {
  if (n <= 1) return 0
  const span = box.width - box.left - box.right
  const i = Math.round(((x - box.left) / span) * (n - 1))
  return Math.max(0, Math.min(n - 1, i))
}

/** Widths in percent for a segmented bar; every non-empty band keeps at least a sliver so it can be clicked. */
export function segmentWidths(buckets: Pick<Bucket, 'count'>[], minPct = 4): number[] {
  const total = buckets.reduce((n, b) => n + b.count, 0)
  if (total === 0) return buckets.map(() => 0)
  const raw = buckets.map((b) => (b.count / total) * 100)
  const lifted = raw.map((w, i) => (buckets[i].count > 0 && w < minPct ? minPct : w))
  const sum = lifted.reduce((n, w) => n + w, 0)
  return lifted.map((w) => (w / sum) * 100)
}

/** The list rows to show for a focus: those whose id is in the set; the order of the list is kept. */
export function rowsInFocus<T>(rows: T[], id: (row: T) => string, ids: string[]): T[] {
  const set = new Set(ids)
  return rows.filter((r) => set.has(id(r)))
}
